// Generic "targeted error review" core, shared across every module that
// tags its items with some kind of topic/category field. See
// numericalReasoningLogic.js's classifyNumericalErrors/compareWeaknessTrend
// (the original, module-specific version this was generalized from) --
// that file and instrumentComprehensionLogic.js are left as-is, so this is
// additive, not a refactor of working code.
const round1 = (value) => Math.round(value * 10) / 10;

// Generic version of buildNumericalItemBreakdown: pairs each item with its
// response, preserving whatever category field(s) the caller supplies via
// extraFields(item) (e.g. `(item) => ({ topic: item.topic })`) onto the
// shared shape every classify/trend function below expects.
// getCorrectAnswer(item) defaults to item.correctLabel (the field name most
// modules use) but can be overridden for modules keyed differently, e.g.
// Verbal Reasoning's item.correctAnswer.
export const buildItemBreakdown = (items, responses, extraFields = () => ({}), getCorrectAnswer = (item) => item.correctLabel) => {
  const responseById = new Map((responses || []).map((response) => [response.itemId, response]));
  return (items || []).map((item) => {
    const response = responseById.get(item.id);
    const given = response ? response.given : null;
    const correctLabel = getCorrectAnswer(item);
    return {
      id: item.id,
      ...extraFields(item),
      difficulty: item.difficulty,
      prompt: item.prompt,
      given,
      correctLabel,
      correct: given === correctLabel,
      explanation: item.explanation,
      latencyMs: response && typeof response.latencyMs === 'number' ? response.latencyMs : null
    };
  });
};

// Ranks a session's incorrect (answered but wrong) items by category, as
// returned by getCategory(item) => { id, label } | null (null excludes the
// item from classification -- e.g. a category-less item). The
// classification already exists in the item data via getCategory, so this
// is a count/rank, not an inference. Omissions are excluded: skipping a
// question says nothing about which category it's weak on.
export const classifyErrors = (items, getCategory) => {
  const incorrect = (items || []).filter((item) => item.given !== null && item.correct === false);
  if (incorrect.length === 0) return [];

  const counts = new Map();
  incorrect.forEach((item) => {
    const category = getCategory(item);
    if (!category) return;
    const existing = counts.get(category.id) || { id: category.id, label: category.label, count: 0 };
    existing.count += 1;
    counts.set(category.id, existing);
  });

  const totalClassified = [...counts.values()].reduce((sum, entry) => sum + entry.count, 0);
  if (totalClassified === 0) return [];

  return [...counts.values()]
    .map((entry) => ({ ...entry, share: round1((entry.count / totalClassified) * 100) }))
    // id may be a string (topic slug) or a number (a tab/part id) depending
    // on the module -- compare as strings so both sort without throwing.
    .sort((a, b) => b.count - a.count || String(a.id).localeCompare(String(b.id)));
};

// One factual sentence naming the category behind the most incorrect
// answers -- never a claim about *why* beyond that count, since the
// response data can't establish more than that.
export const describeWeakness = (classification, noun = 'question') => {
  if (!classification.length) return null;
  const [top] = classification;
  return `${top.count} of your incorrect answer${top.count === 1 ? '' : 's'} ${top.count === 1 ? 'was' : 'were'} ${top.label} ${noun}${top.count === 1 ? '' : 's'}.`;
};

// Compares this session's top weak category against the account's most
// recent PRIOR assessment attempt for the same category (previousItems --
// see getPreviousAssessmentItems in cbtCommon.js). null with no prior
// attempt to compare against, so a results screen never fabricates a
// "still an issue" claim for a first-ever attempt.
export const compareWeaknessTrend = (currentClassification, previousItems, getCategory) => {
  if (!currentClassification.length || previousItems == null) return null;
  const [top] = currentClassification;
  const previousEntry = classifyErrors(previousItems, getCategory).find((entry) => entry.id === top.id);
  const previousCount = previousEntry ? previousEntry.count : 0;
  const status = previousCount === 0 ? 'new'
    : top.count < previousCount ? 'improved'
      : top.count > previousCount ? 'worse'
        : 'unchanged';
  return { id: top.id, label: top.label, currentCount: top.count, previousCount, status };
};

export const describeWeaknessTrend = (trend) => {
  if (!trend) return null;
  switch (trend.status) {
    case 'improved': return `${trend.label} errors are down from your last attempt (${trend.previousCount} → ${trend.currentCount}).`;
    case 'worse': return `${trend.label} errors are up from your last attempt (${trend.previousCount} → ${trend.currentCount}).`;
    case 'unchanged': return `${trend.label} errors are unchanged from your last attempt (still ${trend.currentCount}).`;
    case 'new': return `${trend.label} wasn't a weak spot in your last attempt.`;
    default: return null;
  }
};
